import { DateSource } from '@shared/app/scripts/services/date-source';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Endpoint } from '@core/app/scripts/services/endpoint';
import { HandleData } from '@core/app/scripts/services/handle-data';
import { Mask } from '@core/app/scripts/services/mask';
import { Moment } from '@shared/app/scripts/vendor/moment';

export class LegacyCustomer {
  constructor(
    private $http: ng.IHttpService,
    private $log: ng.ILogService,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private dateSource: DateSource,
    private displayMessage: DisplayMessage,
    private endpoint: Endpoint,
    private handleData: HandleData,
    private legacy,
    private mask: Mask,
    private moment: Moment,
  ) {}

  spynlEndpoint = this.endpoint.getSpynlEndpoint();

  getDateRangeFilter = (month, year) => {
    const now = this.dateSource.now();

    const startDate = this.moment(now),
      endDate = this.moment(now);

    month = month >= 0 ? month - 1 : this.moment(now).month();
    year = year ? year : this.moment(now).year();

    if (month >= 0) {
      startDate.set('month', month).startOf('month');
      endDate.set('month', month).endOf('month');
    }

    if (year) {
      startDate.set('year', year);
      endDate.set('year', year);
      if (month < 0) {
        startDate.startOf('year');
        endDate.endOf('year');
      }
    }

    return { startDate: startDate.toJSON(), endDate: endDate.toJSON() };
  };

  /**
   * @name posApp.legacyCustomer#loadCustomerSalesHistory
   * @methodOf posApp.legacyCustomer
   *
   * @description
   * Function get the customer's sales history filtered by date
   * @param {string} id the customer's _id
   * @param {string} month the month
   * @param {string} year the year
   */
  loadCustomerSalesHistory = (id, month, year) => {
    const filter: any = {
      customerId: id,
    };

    if (!month && !year) {
      filter.startDate = this.moment().year(1970).startOf('year').toJSON();
      filter.endDate = this.dateSource.now();
    }

    if (month >= 0 && year > 0) {
      Object.assign(filter, this.getDateRangeFilter(month, year));
    }

    return this.$http
      .post(this.spynlEndpoint + '/sales/barcodes-per-customer', filter, {
        withCredentials: true,
      })
      .then((response) => {
        // Only return items of type barcode
        // An id is only needed for showing which item is selected in the view
        return response.data.data.filter((item, index) => {
          if (item.category === 'barcode') {
            item.id = index;
            return true;
          }
        });
      });
  };

  /**
   * @name posApp.legacyCustomer#loadCustomerTransactionHistory
   * @methodOf posApp.legacyCustomer
   *
   * @description
   * Function get the customer's transaction history by date and warehouse
   * @param {string} customerId the customer's _id
   * @param {string} type the transaction type
   * @param {string} warehouse the warehouse
   * @param {string} month the month
   * @param {string} year the year
   */
  loadCustomerTransactionHistory = (
    customerId: string,
    type: number,
    warehouse: string,
    month,
    year,
    status,
  ) => {
    const filter: any = {
      customerId,
    };

    let resource = 'retail-transactions';

    if (month >= 0 && year > 0) {
      Object.assign(filter, this.getDateRangeFilter(month, year));
    }

    if (warehouse) {
      filter.warehouseId = warehouse;
    }

    if (type === 9) {
      resource = 'consignments';
      filter.status = status;
    } else if (type) {
      filter.type = type;
    }

    return this.handleData.getData(
      {
        resource,
      },
      filter,
      null,
      null,
      {
        sort: [{ field: 'created.date', direction: -1 }],
        fields: [
          '_id',
          'created',
          'nr',
          'overallReceiptDiscount',
          'payments',
          'shop',
          'status',
          'totalAmount',
          'totalCoupon',
          'totalDiscount',
          'totalDiscountCoupon',
          'totalPaid',
          'totalStoreCreditPaid',
          'type',
        ],
      },
    );
  };

  /**
   * @name posApp.legacyCustomer#loadCustomerCouponHistory
   * @methodOf posApp.legacyCustomer
   *
   * @description
   * Function retrieves the customer's coupons
   * @param {string} customerId the customer's UUID
   */
  loadCustomerCouponHistory = (customerId) => {
    return this.legacy.getCustomerCoupons(customerId);
  };

  /**
   * @name posApp.legacyCustomer#get
   * @methodOf posApp.legacyCustomer
   *
   * @description
   * Function which loads the customer data by email, loyalty number, or id
   * @param {string} customerId the unique id of the customer
   * @param {string} email the email of the customer
   * @param {string} loyaltyNo the loyalty card number of the customer
   */
  get = (customerId, email, loyaltyNo) => {
    if (!customerId && !email && !loyaltyNo) {
      this.displayMessage.message(
        this.$translate.instant('ClientNumberNotFound'),
      );
      this.$log.error('[getCustomer] No search criteria provided!');
      return this.$q.when();
    }

    const filter: any = { active: true };

    if (customerId) {
      filter._id = customerId;
    } else if (email) {
      filter['contacts.email'] = email;
    } else if (loyaltyNo) {
      filter.loyalty_no = loyaltyNo;
    }

    this.displayMessage.showLoading();

    return this.handleData
      .getData(
        {
          resource: 'customers',
        },
        filter,
      )
      .then((response) => {
        this.mask.hide();
        if (response.data && response.data.data.length > 0) {
          const data = response.data.data[0];
          const dob = data.dob;
          if (dob) {
            data.dob = new Date(dob);
          }
          return data;
        } else {
          this.displayMessage.message(
            this.$translate.instant('ClientNumberNotFound'),
          );
          return false;
        }
      });
  };

  /**
   * @name posApp.legacyCustomer#save
   * @methodOf posApp.legacyCustomer
   *
   * @description
   * Function saves a customer object
   * @param {object} customerToSave the customer object to save
   */
  save = (customerToSave) => {
    if (customerToSave.dob) {
      // format date to ISO string
      customerToSave.dob = this.moment(customerToSave.dob)
        .format()
        .slice(0, 10);
    }

    for (const a in customerToSave.addresses) {
      customerToSave.addresses[a].zipcode = customerToSave.addresses[a].zipcode
        .replace(/\s/g, '')
        .toUpperCase();
    }

    return this.handleData.saveData(
      {
        resource: 'customers',
      },
      customerToSave,
    );
  };
}

/**
 * @ngdoc service
 * @name posApp.legacyCustomer
 * @description
 * A service for the customer resource
 */
angular
  .module('posApp')
  .factory(
    'legacyCustomer',
    (
      $http: ng.IHttpService,
      $log: ng.ILogService,
      $q: ng.IQService,
      translate: ng.translate.ITranslateService,
      dateSource: DateSource,
      displayMessage: DisplayMessage,
      endpoint: Endpoint,
      handleData: HandleData,
      legacy,
      mask: Mask,
      moment: Moment,
    ) => {
      return new LegacyCustomer(
        $http,
        $log,
        $q,
        translate,
        dateSource,
        displayMessage,
        endpoint,
        handleData,
        legacy,
        mask,
        moment,
      );
    },
  );
